import { useEffect, useState, type FC } from "react";
import { IntervalToggleContainer } from "../CronGenerator.style";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import React from "react";

type CronValues = {
    minute: string;
    hour: string;
    day: string;
    month: string;
    weekDay: string;
};

type MinuteProps = {
    setCronValues: React.Dispatch<React.SetStateAction<CronValues>>;
    clear: boolean
};

const MinuteOptions = {
    EVERY: "Minute",
    RANGE: "Range",
} as const;

const Minutes: FC<MinuteProps> = ({ setCronValues, clear }) => {
    const [activeOption, setActiveOption] = useState<string>(MinuteOptions.EVERY);
    const [error, setError] = useState(false);
    const [prefixError, setPrefixError] = useState(false);
    const [minute, setMinute] = useState("*");
    const [prefixInterval, setPrefixInterval] = useState("*");
    const [minuteRange, setMinuteRange] = useState({ from: "", to: "" });
    const [minuteRangeError, setMinuteRangeError] = useState({ fromError: false, toError: false });
    const [startAtEnabled, setStartAtEnabled] = useState<boolean>(false);
    useEffect(() => {
        setMinute("*");
        setPrefixInterval("*");
        setError(false);
        setPrefixError(false);
        setMinuteRange({ from: "", to: "" });
        setMinuteRangeError({ fromError: false, toError: false });
        setStartAtEnabled(false)
    }, [activeOption, clear]);

    useEffect(() => {
        if (activeOption === MinuteOptions.EVERY) {
            const minuteVal =
                prefixInterval === "*" || prefixInterval === ""
                    ? minute
                    : minute === "*" || minute === ""
                        ? minute
                        : `${minute}/${prefixInterval}`;
            setCronValues((prev) => ({ ...prev, minute: minuteVal }));
        }
    }, [minute, prefixInterval, activeOption, setCronValues]);

    useEffect(() => {
        if (activeOption === MinuteOptions.RANGE) {
            let rangeVal = "*";
            const from = Number(minuteRange.from);
            const to = Number(minuteRange.to);
            if (from === 0 || to === 0) {
                rangeVal = "*"
            }
            else if (!isNaN(from) && !isNaN(to) && from <= to && to <= 59) {
                rangeVal = `${from}-${to}`;
            }
            setCronValues((prev) => ({ ...prev, minute: rangeVal }));
        } else {
            setCronValues((prev) => ({ ...prev, minute: "*" }));
        }
    }, [minuteRange, activeOption, setCronValues]);

    const handleOptionSelect = (event: SelectChangeEvent) => {
        setActiveOption(event.target.value);
    };

    const handleNumericInput = (
        event: React.ChangeEvent<HTMLInputElement>,
        min: number,
        max: number,
        setValue: (val: string) => void,
        setError: (err: boolean) => void
    ) => {
        const val = event.target.value;
        const num = Number(val);
        if (val === "") {
            setValue("*");
            setError(false);
        } else if (!isNaN(num) && num >= min && num <= max) {
            setValue(val);
            setError(false);
        } else {
            setValue("*");
            setError(true);
        }
    };

    const validateMinute = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = Number(e.target.value)
        if (value >= 0 && value <= 23) {
            setStartAtEnabled(true)
        } else {
            setStartAtEnabled(false)
        }
        handleNumericInput(e, 0, 59, setMinute, setError);
    }

    const addPrefix = (e: React.ChangeEvent<HTMLInputElement>) =>
        handleNumericInput(e, 1, 59, setPrefixInterval, setPrefixError);

    const handleMinuteFromRange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const from = e.target.value;
        const num = Number(from);
        const toNum = Number(minuteRange.to);

        setMinuteRange((prev) => ({ ...prev, from }));
        const fromError = !(from === "" || (num >= 0 && num <= 59));
        if (fromError) {
            setStartAtEnabled(false);
        } else {
            setStartAtEnabled(true);
        }
        const toError = from !== "" && (toNum < num || toNum > 59);

        setMinuteRangeError({ fromError, toError });
    };

    const handleMinuteToRange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const to = e.target.value;
        const toNum = Number(to);
        const fromNum = Number(minuteRange.from);

        setMinuteRange((prev) => ({ ...prev, to }));
        const toError =
            isNaN(toNum) || toNum < fromNum || toNum > 59;

        setMinuteRangeError((prev) => ({ ...prev, toError }));
    };

    const renderSelector = () => {
        switch (activeOption) {
            case MinuteOptions.EVERY:
                return (
                    <>
                        <TextField
                            label="Minutes"
                            variant="standard"
                            sx={{ m: 1, width: "fit-content" }}
                            onChange={validateMinute}
                            error={error}
                            helperText={error ? "0 to 59" : " "}
                            value={minute === "*" ? "" : minute}
                        />
                        <TextField
                            label="Start at"
                            variant="standard"
                            disabled={!startAtEnabled}
                            sx={{ m: 1, width: "fit-content" }}
                            onChange={addPrefix}
                            error={prefixError}
                            helperText={prefixError ? "1 to 59" : "*Minutes"}
                            value={prefixInterval === "*" ? "" : prefixInterval}
                        />
                    </>
                );
            case MinuteOptions.RANGE:
                return (
                    <>
                        <TextField
                            label="From"
                            variant="standard"
                            sx={{ m: 1, width: "fit-content" }}
                            value={minuteRange.from}
                            onChange={handleMinuteFromRange}
                            error={minuteRangeError.fromError}
                            helperText={minuteRangeError.fromError ? "0 to 59" : " "}
                        />
                        <TextField
                            label="To"
                            variant="standard"
                            disabled={!startAtEnabled}
                            sx={{ m: 1, width: "fit-content" }}
                            value={minuteRange.to}
                            onChange={handleMinuteToRange}
                            error={minuteRangeError.toError}
                            helperText={
                                minuteRangeError.toError
                                    ? `${Number(minuteRange.from) < 59 ? Number(minuteRange.from) : 0} to 59`
                                    : " "
                            }
                        />
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <IntervalToggleContainer>
            <FormControl variant="standard" sx={{ m: 1, minWidth: 150 }}>
                <InputLabel id="minute-select-label">Minute</InputLabel>
                <Select
                    labelId="minute-select-label"
                    value={activeOption}
                    onChange={handleOptionSelect}
                >
                    <MenuItem value={MinuteOptions.EVERY}>{MinuteOptions.EVERY}</MenuItem>
                    <MenuItem value={MinuteOptions.RANGE}>{MinuteOptions.RANGE}</MenuItem>
                </Select>
            </FormControl>
            {renderSelector()}
        </IntervalToggleContainer>
    );
};

export default Minutes;
